/**
 * Zone Store - manages network zones state
 *
 * Each zone represents a separate network area with zone_id based data isolation.
 * Only one zone can be active at a time in the frontend.
 * IMPORTANT: No default zone - user must explicitly select a zone.
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { NetworkZone, CreateZoneRequest, UpdateZoneRequest } from '../types/zone';
import { zoneService } from '../services/zoneService';

interface ZoneState {
  /** List of all available zones */
  zones: NetworkZone[];
  /** ID of the currently active zone (determines which data is displayed) */
  activeZoneId: string | null;
  /** Loading state for zone operations */
  isLoading: boolean;
  /** Error message if any zone operation failed */
  error: string | null;
  /** Whether zones have been loaded at least once */
  initialized: boolean;
}

interface ZoneActions {
  /** Load all zones from the backend */
  loadZones: () => Promise<void>;
  /** Select a zone as the active zone */
  selectZone: (zoneId: string) => void;
  /** Clear the active zone (zoneless state) */
  clearZone: () => void;
  /** Get the currently active zone */
  getActiveZone: () => NetworkZone | null;
  /** Create a new zone */
  createZone: (data: CreateZoneRequest) => Promise<NetworkZone>;
  /** Update an existing zone */
  updateZone: (zoneId: string, data: UpdateZoneRequest) => Promise<NetworkZone>;
  /** Delete a zone */
  deleteZone: (zoneId: string) => Promise<void>;
  /** Import a GeoJSON polygon for a zone */
  importPolygon: (zoneId: string, polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon) => Promise<NetworkZone>;
  /** Clear any error state */
  clearError: () => void;
  /** Reset the store to initial state */
  reset: () => void;
}

type ZoneStore = ZoneState & ZoneActions;

const initialState: ZoneState = {
  zones: [],
  activeZoneId: null,
  isLoading: false,
  error: null,
  initialized: false,
};

export const useZoneStore = create<ZoneStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        loadZones: async () => {
          set({ isLoading: true, error: null }, false, 'loadZones:start');

          try {
            const zones = await zoneService.getAllZones();

            const currentActiveId = get().activeZoneId;
            let newActiveId = currentActiveId;

            // Only keep the active zone if it still exists in the loaded zones
            // NO FALLBACK - if zone doesn't exist, go to zoneless state
            if (currentActiveId && !zones.find(z => z.id === currentActiveId)) {
              newActiveId = null;
            }

            set({
              zones,
              activeZoneId: newActiveId,
              isLoading: false,
              initialized: true,
            }, false, 'loadZones:success');
          } catch (error) {
            console.error('[ZoneStore] Failed to load zones:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Zonen konnten nicht geladen werden',
            }, false, 'loadZones:error');
          }
        },

        selectZone: (zoneId: string) => {
          const zones = get().zones;
          const zone = zones.find(z => z.id === zoneId);

          if (!zone) {
            console.error(`[ZoneStore] Zone not found: ${zoneId}`);
            return;
          }

          set({ activeZoneId: zoneId }, false, 'selectZone');
        },

        clearZone: () => {
          set({ activeZoneId: null }, false, 'clearZone');
        },

        getActiveZone: () => {
          const { zones, activeZoneId } = get();
          if (!activeZoneId) return null;
          return zones.find(z => z.id === activeZoneId) || null;
        },

        createZone: async (data: CreateZoneRequest) => {
          set({ isLoading: true, error: null }, false, 'createZone:start');

          try {
            const newZone = await zoneService.createZone(data);

            set(state => ({
              zones: [...state.zones, newZone],
              isLoading: false,
            }), false, 'createZone:success');

            return newZone;
          } catch (error) {
            console.error('[ZoneStore] Failed to create zone:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Zone konnte nicht erstellt werden',
            }, false, 'createZone:error');
            throw error;
          }
        },

        updateZone: async (zoneId: string, data: UpdateZoneRequest) => {
          set({ isLoading: true, error: null }, false, 'updateZone:start');

          try {
            const updatedZone = await zoneService.updateZone(zoneId, data);

            set(state => ({
              zones: state.zones.map(z => z.id === zoneId ? updatedZone : z),
              isLoading: false,
            }), false, 'updateZone:success');

            return updatedZone;
          } catch (error) {
            console.error('[ZoneStore] Failed to update zone:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Zone konnte nicht aktualisiert werden',
            }, false, 'updateZone:error');
            throw error;
          }
        },

        deleteZone: async (zoneId: string) => {
          // No default zone protection - any zone can be deleted
          set({ isLoading: true, error: null }, false, 'deleteZone:start');

          try {
            await zoneService.deleteZone(zoneId);

            const { activeZoneId, zones } = get();
            const remainingZones = zones.filter(z => z.id !== zoneId);

            // If the deleted zone was active, clear to zoneless state (NO FALLBACK)
            let newActiveId = activeZoneId;
            if (activeZoneId === zoneId) {
              newActiveId = null;
            }

            set({
              zones: remainingZones,
              activeZoneId: newActiveId,
              isLoading: false,
            }, false, 'deleteZone:success');
          } catch (error) {
            console.error('[ZoneStore] Failed to delete zone:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Zone konnte nicht gelöscht werden',
            }, false, 'deleteZone:error');
            throw error;
          }
        },

        importPolygon: async (zoneId: string, polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon) => {
          set({ isLoading: true, error: null }, false, 'importPolygon:start');

          try {
            const updatedZone = await zoneService.importPolygon(zoneId, polygon);

            set(state => ({
              zones: state.zones.map(z => z.id === zoneId ? updatedZone : z),
              isLoading: false,
            }), false, 'importPolygon:success');

            return updatedZone;
          } catch (error) {
            console.error('[ZoneStore] Failed to import polygon:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Polygon konnte nicht importiert werden',
            }, false, 'importPolygon:error');
            throw error;
          }
        },

        clearError: () => {
          set({ error: null }, false, 'clearError');
        },

        reset: () => {
          set(initialState, false, 'reset');
        },
      }),
      {
        name: 'zone-storage',
        // Only persist activeZoneId, not the full zones list
        partialize: (state) => ({ activeZoneId: state.activeZoneId }),
      }
    ),
    { name: 'ZoneStore' }
  )
);

// Export selector hooks for convenience
export const useActiveZone = () => useZoneStore(state => state.getActiveZone());
export const useActiveZoneId = () => useZoneStore(state => state.activeZoneId);
export const useZones = () => useZoneStore(state => state.zones);
export const useZoneLoading = () => useZoneStore(state => state.isLoading);
